/* eslint-disable no-unused-vars */
import { useCallback ,useEffect, useState } from "react";
import api from "../../../services/api.service";

// Import Modals
import AddHospitalModal from "../../modals/AddHospitalModal";
import EditHospitalModal from "../../modals/EditHospitalModal";
import ConfirmRemoveModal from "../../modals/ConfirmRemoveModal";
import PaginationUser from "../../modals/PaginationModal";
import GeneralModal from "../../modals/GeneralModal";

function HospitalManagement() {
  // -----------------------------
  // State สำหรับควบคุม Modal
  // -----------------------------
  const [showAddHospitalModal, setShowAddHospitalModal] = useState(false);
  const [showEditHospitalModal, setShowEditHospitalModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // -----------------------------
  // State สำหรับข้อมูล Hospital
  // -----------------------------
  const [hospitals, setHospitals] = useState([]);
  const [newHospital, setNewHospital] = useState({
    hospitalNameEN: "",
    hospitalNameTH: "",
    hospitalEmail: "",
    isActive: true,
  });
  const [editHospital, setEditHospital] = useState(null);
  const [deleteHospitalId, setDeleteHospitalId] = useState(null);

  // -----------------------------
  // State สำหรับฟิลเตอร์, การค้นหา, การแสดงผล
  // -----------------------------
  const [filteredHospitals, setFilteredHospitals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // -----------------------------
  // State สำหรับ Pagination
  // -----------------------------
  const [currentPage, setCurrentPage] = useState(1);
  const [hospitalsPerPage, setHospitalsPerPage] = useState(8);

  // -----------------------------
  // State สำหรับ General Modal
  // -----------------------------
  const [generalModal, setGeneralModal] = useState({
    isVisible: false,
    message: "",
    isSuccess: true,
    onClose: null,
  });

  const [errors, setErrors] = useState({});

  const showGeneralModal = (message, isSuccess = true, onCloseCallback = null) => {
    setGeneralModal({
      isVisible: true,
      message,
      isSuccess,
      onClose: onCloseCallback,
    });
  };

  const handleCloseGeneralModal = () => {
    setGeneralModal((prev) => ({ ...prev, isVisible: false }));
    if (generalModal.onClose) {
      generalModal.onClose();
    }
  };

  // =========================================================
  // 1) ฟังก์ชันดึงข้อมูลโรงพยาบาลจาก Backend
  // =========================================================
  const fetchHospitals = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/hospitals/");
      setHospitals(response.data.hospitals || []);
      setFilteredHospitals(response.data.hospitals || []);
    } catch (error) {
      console.error("Error fetching hospitals:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHospitals();
  }, []);

  // =========================================================
  // 2) ฟังก์ชันสำหรับการกรองข้อมูลโรงพยาบาลตามเงื่อนไข
  // =========================================================
  const filterHospitals = useCallback(() => {
    let filtered = [...hospitals];
    if (searchQuery) {
        filtered = filtered.filter((hospital) => {
          const query = searchQuery.toLowerCase();
          return (
            (hospital.hospitalNameEN && hospital.hospitalNameEN.toLowerCase().includes(query)) ||
            (hospital.hospitalNameTH && hospital.hospitalNameTH.toLowerCase().includes(query)) ||
            (hospital.hospitalEmail && hospital.hospitalEmail.toLowerCase().includes(query)) ||
            (hospital.hospitalId && hospital.hospitalId.toLowerCase().includes(query))
          );
        });
      }
    if (selectedStatus) {
      const isActive = selectedStatus === "true";
      filtered = filtered.filter((hospital) => hospital.isActive === isActive);
    }
    setFilteredHospitals(filtered);
    setCurrentPage(1);
  }, [hospitals, searchQuery, selectedStatus]);

  useEffect(() => {
    filterHospitals();
  }, [filterHospitals]);

  // =========================================================
  // 3) Pagination Logic
  // =========================================================
  const indexOfLastHospital = currentPage * hospitalsPerPage;
  const indexOfFirstHospital = indexOfLastHospital - hospitalsPerPage;
  const currentHospitals = filteredHospitals.slice(indexOfFirstHospital, indexOfLastHospital);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(filteredHospitals.length / hospitalsPerPage)) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  // =========================================================
  // 4) ฟังก์ชันเพิ่มโรงพยาบาลใหม่
  // =========================================================
  const validateForm = (hospitalData) => {
    const errors = {};

    if (!hospitalData.hospitalNameEN || hospitalData.hospitalNameEN.trim() === '') {
      errors.hospitalNameEN = "Hospital Name (EN) is required";
    }
    if (!hospitalData.hospitalNameTH || hospitalData.hospitalNameTH.trim() === '') {
      errors.hospitalNameTH = "Hospital Name (TH) is required";
    }

    if (hospitalData.hospitalEmail) {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(hospitalData.hospitalEmail)) {
        errors.hospitalEmail = "Invalid email format";
      }
    }

    return errors;
  };

  const handleAddHospital = async () => {
    const validationErrors = validateForm(newHospital);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setErrors({});
      await api.post("/hospitals/create", newHospital);
      await fetchHospitals();
      handleCancelAddHospital();
      showGeneralModal("Hospital added successfully", true);
    } catch (error) {
      console.error("Error adding hospital:", error);
      const errorMessage = error.response?.data?.message || "Failed to add hospital";
      showGeneralModal(errorMessage, false);
    }
  };

  const handleCancelAddHospital = () => {
    setShowAddHospitalModal(false);
    setNewHospital({
      hospitalNameEN: "",
      hospitalEmail: "",
      isActive: true,
    });
    setErrors({});
  };

  // =========================================================
  // 5) ฟังก์ชันแก้ไขโรงพยาบาล
  // =========================================================
  const handleEditHospital = (hospitalId) => {
    const hospital = hospitals.find((h) => h._id === hospitalId);
    if (hospital) {
      setEditHospital(hospital);
      setShowEditHospitalModal(true);
    }
  };

  const handleSaveEditHospital = async () => {
    try {
      await api.put(`/hospitals/update/${editHospital._id}`, {
        hospitalNameEN: editHospital.hospitalNameEN,
        hospitalNameTH: editHospital.hospitalNameTH,
        hospitalEmail: editHospital.hospitalEmail,
        isActive: editHospital.isActive,
      });
      await fetchHospitals();
      setShowEditHospitalModal(false);
      setEditHospital(null);
      showGeneralModal("Hospital updated successfully", true);
    } catch (error) {
      console.error("Error updating hospital:", error);
      setShowEditHospitalModal(false);
      showGeneralModal(
        error.response?.data?.message || "Failed to update hospital",
        false,
        () => {
          setShowEditHospitalModal(true);
        }
      );
    }
  };

  // =========================================================
  // 6) ฟังก์ชันลบโรงพยาบาล
  // =========================================================
  const handleDeleteHospital = async () => {
    try {
      await api.delete(`/hospitals/${deleteHospitalId}`);
      setHospitals((prev) => prev.filter((hospital) => hospital._id !== deleteHospitalId));
      setShowConfirmModal(false);
      setDeleteHospitalId(null);
      showGeneralModal("Hospital deleted successfully", true);
    } catch (error) {
      console.error("Error deleting hospital:", error);
      showGeneralModal(error.response?.data?.message || "Failed to delete hospital", false);
    }
  };

  const handleOpenDeleteModal = (hospitalId) => {
    setDeleteHospitalId(hospitalId);
    setShowConfirmModal(true);
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setDeleteHospitalId(null);
  };

  // =========================================================
  // 7) ฟังก์ชัน Clear Filters
  // =========================================================
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedStatus("");
  };

  // =========================================================
  // สุดท้ายคือส่วน Render
  // =========================================================
  return (
<>
      <div className="container mx-auto max-w-screen-xl px-3 sm:px-6 md:px-8 py-6 sm:py-8 my-4 sm:my-6">
        <div className="bg-white shadow-md rounded-xl p-4 sm:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-baseline">
              <i className="fas fa-truck-medical text-xl sm:text-2xl mr-3 sm:mr-4"></i>
              <h1 className="text-lg sm:text-xl font-bold">Hospital Management</h1>
            </div>
            <button
              className="btn btn-neutral bg-black w-full sm:w-auto text-sm sm:text-base"
              onClick={() => setShowAddHospitalModal(true)}
            >
              <i className="fas fa-plus mr-2"></i>  
              Add Hospital
            </button>
          </div>

          {/* Search & Filters */}
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search by hospital name or email"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input input-bordered w-full pl-10 text-sm sm:text-base"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <i className="fas fa-search"></i>
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="select select-bordered w-full sm:w-48 text-sm sm:text-base"
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
                <button 
                  className="btn btn-outline btn-error w-full sm:w-auto text-sm sm:text-base" 
                  onClick={clearFilters}
                >
                  <i className="fa-solid fa-filter-circle-xmark mr-2"></i>
                  <span className="hidden sm:inline">Clear Filters</span>
                  <span className="sm:hidden">Clear</span>
                </button>
              </div>
            </div>

            {/* Loading & Data Table */}
            {isLoading ? (
              <div className="flex justify-center items-center h-48 sm:h-64">
                <div className="flex flex-col items-center">
                  <span className="loading loading-spinner loading-lg text-primary"></span>
                  <p className="mt-4 text-gray-600 text-sm sm:text-base">Loading hospitals data...</p>
                </div>
              </div>
            ) : currentHospitals.length === 0 ? (
              <div className="flex justify-center items-center h-48 sm:h-64">
                <p className="text-gray-500 text-sm sm:text-base">No hospitals found</p>
              </div>
            ) : (
              <div className="mt-6 overflow-x-auto">
                <table className="table table-pin-rows table-pin-cols w-full text-sm sm:text-base">
                  <thead>
                    <tr>
                      <th className="text-xs sm:text-sm">#</th>
                      <th className="text-xs sm:text-sm hidden md:table-cell">Hospital ID</th>
                      <th className="text-xs sm:text-sm">Hospital Name (EN)</th>
                      <th className="text-xs sm:text-sm hidden sm:table-cell">Hospital Name (TH)</th>
                      <th className="text-xs sm:text-sm hidden md:table-cell">Email</th>
                      <th className="text-xs sm:text-sm">Status</th>
                      <th className="text-xs sm:text-sm hidden lg:table-cell">Created</th>
                      <th className="text-xs sm:text-sm hidden lg:table-cell">Modified</th>
                      <th className="text-xs sm:text-sm">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentHospitals.map((hospital, index) => (
                      <tr key={hospital._id} className="text-xs sm:text-sm">
                        <th>{indexOfFirstHospital + index + 1}</th>
                        <td className="whitespace-nowrap hidden md:table-cell">{hospital.hospitalId}</td>
                        <td className="whitespace-nowrap">{hospital.hospitalNameEN}</td>
                        <td className="whitespace-nowrap hidden sm:table-cell">{hospital.hospitalNameTH}</td>
                        <td className="whitespace-nowrap hidden md:table-cell">
                          {hospital.hospitalEmail || "-"}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              hospital.isActive
                                ? "badge-accent badge-outline"
                                : "badge-error badge-outline"
                            } text-xs`}
                          >
                            {hospital.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap hidden lg:table-cell">
                          {new Date(hospital.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td className="whitespace-nowrap hidden lg:table-cell">
                          {new Date(hospital.updatedAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td>
                          <div className="flex gap-2 justify-center sm:justify-start">
                            <div className="tooltip" data-tip="Edit Hospital">
                              <button
                                className="btn bg-gray-300 hover:bg-gray-100 btn-xs sm:btn-sm"
                                onClick={() => handleEditHospital(hospital._id)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            </div>
                            <div className="tooltip" data-tip="Remove Hospital">
                              <button
                                className="btn bg-red-500 hover:bg-red-300 btn-xs sm:btn-sm"
                                onClick={() => handleOpenDeleteModal(hospital._id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Modals */}
            <GeneralModal
              isVisible={generalModal.isVisible}
              message={generalModal.message}
              onClose={handleCloseGeneralModal}
              isSuccess={generalModal.isSuccess}
              className="z-[60]"
            />

            {showAddHospitalModal && (
              <AddHospitalModal
                newHospital={newHospital}
                setNewHospital={setNewHospital}
                handleAddHospital={handleAddHospital}
                handleCancelAddHospital={handleCancelAddHospital}
                errors={errors}
              />
            )}

            {showEditHospitalModal && (
              <EditHospitalModal
                editHospital={editHospital}
                setEditHospital={setEditHospital}
                handleSaveEditHospital={handleSaveEditHospital}
                setShowEditHospitalModal={setShowEditHospitalModal}
              />
            )}

            {showConfirmModal && (
              <ConfirmRemoveModal
                handleDeleteUser={handleDeleteHospital}
                handleCloseModal={handleCloseModal}
              />
            )}
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <PaginationUser
              currentPage={currentPage}
              totalUsers={filteredHospitals.length}
              usersPerPage={hospitalsPerPage}
              handlePreviousPage={handlePreviousPage}
              handleNextPage={handleNextPage}
              paginate={paginate}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default HospitalManagement;